import { useCallback, useMemo, useState } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ReactElement } from 'react'
import type { Layout, Layouts } from 'react-grid-layout'
import type { DeviceType } from '~/types/device-type'
import type { IAppEvent } from '~/types/event'
import type { IBuilderConfig, IDataBuilderConfig, IGetSeriesDataBody, IGraphBuilderConfig } from '~/types/graph-builder'

import DynamicDataBlock from '~/components/DynamicDataBlock'
import DynamicGraphBlock from '~/components/DynamicGraphBlock'
import Section from '~/components/Section'
import { COMMON_GRID_PROPS } from '~/const'
import { generateLayout, getDefaultLayout } from '~/utils/personal-dashboard'
import { capitalize } from '~/utils/string'

const ResponsiveGridLayout = WidthProvider(Responsive)

interface Props {
  dataType?: 'data' | 'graph'
  graphConfigs: IGraphBuilderConfig[]
  graphOptions: <T extends IBuilderConfig>(
    config: T
  ) => {
    apiSelectionFn?: (body: IGetSeriesDataBody[]) => Promise<any>
    appKey: string
    config: T
    deviceType: DeviceType
    eventsData?: IAppEvent[]
    isAppLoading: boolean
    kpiField: string
  }
  isResizable?: boolean
  layout: Layouts
  onLayoutsChange: (layouts: Layouts, sectionName?: string) => void
  sectionName?: string
}

const DynamicDataLayout = ({
  dataType = 'graph',
  graphConfigs,
  graphOptions,
  isResizable = true,
  layout,
  onLayoutsChange,
  sectionName,
}: Props): ReactElement => {
  /** Local state */

  const [isFirstRender, setIsFirstRender] = useState(true)

  const layouts = useMemo(
    () => (layout ? generateLayout(layout, graphConfigs) : getDefaultLayout(graphConfigs)),
    [graphConfigs, layout]
  )

  /** Handlers */

  const renderDataByGraphType = useCallback(
    (config: IGraphBuilderConfig) =>
      config.series?.length === 1 && config.series[0].graphType === 'raw' ? (
        <DynamicDataBlock {...graphOptions(config as IDataBuilderConfig)} />
      ) : (
        <DynamicGraphBlock {...graphOptions(config as IGraphBuilderConfig)} />
      ),
    [graphOptions]
  )

  const handleLayoutChange = useCallback(
    async (_layout: Layout[], layouts: Layouts) => {
      if (!isFirstRender) {
        onLayoutsChange(layouts, sectionName)
      }

      if (isFirstRender) {
        setIsFirstRender(false)
      }
    },
    [isFirstRender, onLayoutsChange, sectionName]
  )

  /** Render */

  return (
    <Section title={capitalize(sectionName)}>
      <ResponsiveGridLayout
        {...COMMON_GRID_PROPS}
        isBounded
        isDraggable
        isResizable={isResizable}
        layouts={layouts}
        onLayoutChange={handleLayoutChange}
        rowHeight={dataType === 'graph' ? 75.5 : COMMON_GRID_PROPS.rowHeight}
      >
        {graphConfigs.map((config) => (
          <div key={config.id}>{renderDataByGraphType(config)}</div>
        ))}
      </ResponsiveGridLayout>
    </Section>
  )
}

export default DynamicDataLayout
